import { Component, computed, inject, input, InputSignal, linkedSignal, Signal } from '@angular/core';
import { Panel as PanelService } from './panel.svc';

type PanelStyle = 'flat' | 'floating';
interface PanelHeader {
  projected: null | boolean;
  title: null | string;
}
interface PanelState {
  header: PanelHeader;
  collapsible: {
    doDestroy: boolean;
    is: boolean;
    isListening: boolean;
  };
}
export { PanelStyle, PanelHeader, PanelState };

@Component({
  selector: 'oke-panel',
  providers: [PanelService],
  host: {
    '[class]': '"style--" + this.$styleInput()',
    '[class.is-collapsed]': 'this.$state().collapsible.is',
    '[style.--base-color]': 'this.$baseColorInput()'
  },
  templateUrl: './panel.html',
  styleUrl: './panel.scss'
})
export class Panel {
  private readonly _panelSvc: PanelService = inject(PanelService);
  protected readonly $state: Signal<PanelState>;
  readonly $baseColorInput: InputSignal<string> = input<string>('var(--oneok-navy)', { alias: 'base-color' });
  readonly $isCollapsedInput: InputSignal<boolean> = input<boolean>(false, { alias: 'is-collapsed' });
  readonly $isDestroyOnCollapseInput: InputSignal<boolean> = input<boolean>(false, { alias: 'is-destroy-on-collapse' });
  readonly $styleInput: InputSignal<PanelStyle> = input<PanelStyle>('flat', { alias: 'panel-style' });
  readonly $titleInput: InputSignal<string | null> = input<string | null>(null, { alias: 'panel-title' });

  constructor() {
    this._panelSvc.initState(
      linkedSignal<
        {
          isCollapsedInput: boolean;
          isDestroyOnCollapseInput: boolean;
          titleInput: string | null;
        },
        PanelState
      >({
        source: () => ({
          isCollapsedInput: this.$isCollapsedInput(),
          isDestroyOnCollapseInput: this.$isDestroyOnCollapseInput(),
          titleInput: this.$titleInput()
        }),
        computation: (source, previous) => {
          const {
            isCollapsedInput: isSourceCollapsed,
            isDestroyOnCollapseInput: isSourceDestroyOnCollapse,
            titleInput: sourceTitle
          } = source;
          return {
            header: {
              projected: false,
              title: previous ? previous.value.header.title : sourceTitle
            },
            collapsible: {
              doDestroy: previous ? previous.value.collapsible.doDestroy : isSourceDestroyOnCollapse,
              is: previous ? previous.value.collapsible.is : isSourceCollapsed,
              isListening: previous ? previous.value.collapsible.isListening : false
            }
          };
        }
      })
    );
    this.$state = computed(() => this._panelSvc.$state());
  }
}
